if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.currentIndex = 0;
        this.touchStartX = null;
        this.touchStartY = null;
        this.touchBlocked = false;

        this.addEventListener('click', (event) => {
          const previous = event.target.closest('[data-lightbox-prev]');
          const next = event.target.closest('[data-lightbox-next]');
          if (previous) {
            event.preventDefault();
            event.stopPropagation();
            this.showRelative(-1);
          } else if (next) {
            event.preventDefault();
            event.stopPropagation();
            this.showRelative(1);
          }
        });

        this.addEventListener('keydown', (event) => {
          if (!this.hasAttribute('open')) return;
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.showRelative(-1);
          } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.showRelative(1);
          }
        });

        this.addEventListener('touchstart', (event) => this.onTouchStart(event), { passive: true });
        this.addEventListener('touchend', (event) => this.onTouchEnd(event), { passive: true });
      }

      hide() {
        this.resetTouch();
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      getMediaItems() {
        return Array.from(this.querySelectorAll('.product-media-modal__media[data-media-id]'));
      }

      showActiveMedia() {
        const items = this.getMediaItems();
        if (!items.length) return;

        const mediaId = this.openedBy?.getAttribute('data-media-id');
        const requestedIndex = items.findIndex((item) => item.getAttribute('data-media-id') === mediaId);
        this.setActiveIndex(requestedIndex >= 0 ? requestedIndex : 0);
      }

      setActiveIndex(index) {
        const items = this.getMediaItems();
        if (!items.length) return;

        this.currentIndex = ((index % items.length) + items.length) % items.length;
        const mobile = window.matchMedia('(max-width: 749px)').matches;

        items.forEach((item, itemIndex) => {
          const active = itemIndex === this.currentIndex;
          item.classList.toggle('active', active);
          if (mobile) item.setAttribute('aria-hidden', active ? 'false' : 'true');
          else item.removeAttribute('aria-hidden');
        });

        const activeMedia = items[this.currentIndex];
        const deferred = activeMedia.matches('deferred-media') ? activeMedia : activeMedia.querySelector('deferred-media');
        if (deferred?.loadContent) deferred.loadContent();

        if (mobile) {
          const content = this.querySelector('.product-media-modal__content');
          if (content) {
            content.scrollTop = 0;
            content.scrollLeft = 0;
          }
        } else {
          activeMedia.scrollIntoView({ block: 'center' });
        }

        const previous = this.querySelector('[data-lightbox-prev]');
        const next = this.querySelector('[data-lightbox-next]');
        const counter = this.querySelector('[data-lightbox-counter]');
        const hasMultiple = items.length > 1;

        if (previous) previous.hidden = !hasMultiple;
        if (next) next.hidden = !hasMultiple;
        if (counter) {
          counter.hidden = !hasMultiple;
          counter.textContent = hasMultiple ? `${this.currentIndex + 1} / ${items.length}` : '';
        }
      }

      showRelative(delta) {
        this.setActiveIndex(this.currentIndex + delta);
      }

      onTouchStart(event) {
        if (!this.hasAttribute('open') || event.touches.length !== 1) return;
        if (!event.target.closest('.product-media-modal__content')) return;

        this.touchBlocked = Boolean(event.target.closest('video, iframe, model-viewer, input, button, a'));
        if (this.touchBlocked) return;

        this.touchStartX = event.touches[0].clientX;
        this.touchStartY = event.touches[0].clientY;
      }

      onTouchEnd(event) {
        if (this.touchBlocked || this.touchStartX === null || !event.changedTouches.length) {
          this.resetTouch();
          return;
        }

        const deltaX = event.changedTouches[0].clientX - this.touchStartX;
        const deltaY = event.changedTouches[0].clientY - this.touchStartY;

        if (Math.abs(deltaX) >= 46 && Math.abs(deltaX) > Math.abs(deltaY) * 1.15) {
          this.showRelative(deltaX < 0 ? 1 : -1);
        }
        this.resetTouch();
      }

      resetTouch() {
        this.touchStartX = null;
        this.touchStartY = null;
        this.touchBlocked = false;
      }
    }
  );
}


if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
